import { LAW_BASE_PATH, LAW_PARAGRAPH_TEXT } from '#shared/constants'

const PATH_REGEX = /^\/(?:juridika\/)?(?:no\/)?(lov|forskrift)(?:\/|\?|$)/

const LEGACY_LAW_SLUG_TO_ID: Record<string, string> = {
  aksjeloven: '1997-06-13-44',
  allmennaksjeloven: '1997-06-13-45',
  aml: '2005-06-17-62',
  arveloven: '2019-06-14-21',
  asal: '1997-06-13-45',
  asl: '1997-06-13-44',
  avhl: '1992-07-03-93',
  barneloven: '1981-04-08-7',
  bvl: '2021-06-18-97',
  domstolloven: '1915-08-13-5',
  ekteskapsloven: '1991-07-04-47',
  el: '1991-07-04-47',
  emgll: '2007-06-29-73',
  ferieloven: '1988-04-29-21',
  folketrygdloven: '1997-02-28-19',
  ftrl: '1997-02-28-19',
  fvl: '1967-02-10-000',
  husleieloven: '1999-03-26-17',
  jordloven: '1995-05-12-23',
  jordskifteloven: '2013-06-21-100',
  kommuneloven: '2018-06-22-83',
  konkurranseloven: '2004-03-05-12',
  lignl: '1980-06-13-24',
  pasientskadeloven: '2001-06-15-53',
  pbl: '2008-06-27-71',
  pl: '1995-08-04-53',
  politiloven: '1995-08-04-53',
  regnskapsloven: '1998-07-17-56',
  rskl: '1998-07-17-56',
  saml: '1965-06-18-6',
  skadeserstatningsloven: '1969-06-13-26',
  skatteloven: '1999-03-26-14',
  skl: '1969-06-13-26',
  sktl: '1999-03-26-14',
  straffeloven: '2005-05-20-28',
  straffeprosessloven: '1981-05-22-25',
  strl: '2005-05-20-28',
  tinglysingsloven: '1935-06-07-2',
  tomtefesteloven: '1996-12-20-106',
  tvisteloven: '2005-06-17-90',
  utlendingsloven: '2008-05-15-35',
  yrkesskadeforsikringsloven: '1989-06-16-65',
}

/**
 * Maps legacy URLs:
 *  - /no/lov/...       -> /{LAW_BASE_PATH}/lov-<id>
 *  - /no/forskrift/... -> /{LAW_BASE_PATH}/forskrift-<id>
 *  - /lov/... -> /{LAW_BASE_PATH}/lov-<id>
 *
 * and normalizes the id to "<kind>-<id>".
 * The parts behind the id segment, like chapters and paragraphs, are not handled per now.
 * Returns the newPath, and an redirectPath.
 */
export function transformLegacyLawPath(url: URL) {
  if (!url || !url.pathname) {
    return null
  }

  const path = decodeURI(url.pathname)
  const query = url.search

  const match = path?.match(PATH_REGEX)
  if (!match || !path) {
    return null
  }

  const kind = match[1]

  const newPath = path.replace(new RegExp(`^/(?:juridika/)?(?:no/)?${kind}(?:/|\\?|$)`), `/${LAW_BASE_PATH}/`)

  const segments = newPath
    .split('/')
    .filter(Boolean)
    .map(s => s.toLowerCase())

  const id = segments[1]
  const mappedId = kind === 'lov' && id ? (LEGACY_LAW_SLUG_TO_ID[id] ?? id) : id

  const prefixedId = mappedId
    ? (mappedId.startsWith(`${kind}-`) ? mappedId : `${kind}-${mappedId}`)
    : undefined

  if (!prefixedId) {
    return `${newPath}${query}`
  }

  const splitParagraph = toSplitParagraphSlug(segments[2] ?? '', segments[3] ?? '')

  const paragraph =
    toParagraphSlug(segments[2] ?? '')
    ?? toParagraphSlug(segments[3] ?? '')
    ?? splitParagraph

  const explicitChapterPath = toChapterPath(segments[2] ?? '', segments[3] ?? '')
  const chapterPath = explicitChapterPath || (splitParagraph ? toChapterPathFromParagraph(splitParagraph) : '')
  const paragraphPath = paragraph ? `/${paragraph}` : ''

  // If legacy path includes more segments (like chapters, paragraphs) etc., we ignore those for now.
  return `/${segments[0]}/${prefixedId}${chapterPath}${paragraphPath}${query}`
}

/**
 * Normalize paragraph segments:
 *  - "paragraf-8-14a" -> "paragraf-8-14a"
 *  - "§ 8-14a"        -> "paragraf-8-14a"
 */
export function toParagraphSlug(segment: string): string | undefined {
  const s = (segment ?? '').trim()
  if (!s) {
    return undefined
  }

  const textMatch = s.match(/^paragraf-(\d+[a-z]?(?:-\d+[a-z]?)*)$/i)
  if (textMatch) {
    return `${LAW_PARAGRAPH_TEXT}-${textMatch[1]}`
  }

  const symbolMatch = s.match(/^§\s*(\d+[a-z]?(?:-\d+[a-z]?)*)$/i)
  if (symbolMatch) {
    return `${LAW_PARAGRAPH_TEXT}-${symbolMatch[1]}`
  }

  return undefined
}

function toSplitParagraphSlug(prefix: string, value: string): string | undefined {
  if (prefix !== LAW_PARAGRAPH_TEXT) {
    return undefined
  }

  const normalizedValue = (value ?? '').trim().toLowerCase()
  if (!/^\d+[a-z]?(?:-\d+[a-z]?)*$/i.test(normalizedValue)) {
    return undefined
  }

  return `${LAW_PARAGRAPH_TEXT}-${normalizedValue}`
}

function toChapterPathFromParagraph(paragraphSlug: string): string {
  const chapterMatch = paragraphSlug.match(/^paragraf-(\d+[a-z]?)-\d+[a-z]?/i)
  if (!chapterMatch) {
    return ''
  }

  return `/kapittel-${chapterMatch[1]}`
}

function toChapterPath(segment: string, nextSegment: string): string {
  const s = (segment ?? '').trim().toLowerCase()
  const n = (nextSegment ?? '').trim().toLowerCase()

  const directMatch = s.match(/^kapittel-(\d+[a-z]?)$/i)
  if (directMatch) {
    return `/kapittel-${directMatch[1]}`
  }

  if (s !== 'kapittel') {
    return ''
  }

  if (!/^\d+[a-z]?$/i.test(n)) {
    return ''
  }

  return `/kapittel-${n}`
}

export function isParagraphSegment(segment: string) {
  return toParagraphSlug(segment) !== undefined
}

export type LegacyBookRedirect =
  | { kind: 'resolved', target: string }
  | { kind: 'unresolved' }

// `bok` and `artikkel` are the only real first segments under /faglitteratur anything else is a legacy book link
const FAGLITTERATUR_REAL_SUBPATHS = new Set(['bok', 'artikkel'])
const LEGACY_FAGLITTERATUR_BOOK_REGEX = /^\/faglitteratur\/([^/]+)\/(\d+)\/?$/

/**
 * Author bios may link to `/faglitteratur/<slug>/<edition>`, but only `/fagbok/<slug>/<edition>` exists.
 * Use the exact edition if possible. Otherwise, use the newest edition for the same book.
 * Return `null` for other paths.
 */
export function resolveLegacyFaglitteraturBook(decodedPath: string, bookLookup: Record<string, string | null | undefined>): LegacyBookRedirect | null {
  const match = decodedPath.match(LEGACY_FAGLITTERATUR_BOOK_REGEX)
  if (!match) {
    return null
  }

  const slug = match[1]
  const edition = match[2]
  if (slug === undefined || edition === undefined || FAGLITTERATUR_REAL_SUBPATHS.has(slug)) {
    return null
  }

  const exact = bookLookup[`/fagbok/${slug}/${edition}`]
  if (exact) {
    return { kind: 'resolved', target: exact }
  }

  const prefix = `/fagbok/${slug}/`
  let newest: { edition: number, target: string } | undefined
  for (const [key, target] of Object.entries(bookLookup)) {
    if (!target || !key.startsWith(prefix)) {
      continue
    }
    const candidate = Number(key.slice(prefix.length))
    if (!Number.isInteger(candidate)) {
      continue
    }
    if (!newest || candidate > newest.edition) {
      newest = { edition: candidate, target }
    }
  }

  return newest ? { kind: 'resolved', target: newest.target } : { kind: 'unresolved' }
}

export function resolveLegacyBookUrl(decodedPath: string, bookLookup: Record<string, string | null | undefined>): LegacyBookRedirect | null {
  const faglitteratur = resolveLegacyFaglitteraturBook(decodedPath, bookLookup)
  if (faglitteratur) {
    return faglitteratur
  }

  if (!decodedPath.startsWith('/fagbok')) {
    return null
  }

  let current = decodedPath.replace(/\/+$/, '')
  while (current && current !== '/') {
    if (!(current in bookLookup)) {
      current = current.slice(0, current.lastIndexOf('/'))
      continue
    }
    const target = bookLookup[current]
    return target ? { kind: 'resolved', target } : { kind: 'unresolved' }
  }

  return { kind: 'unresolved' }
}
